export interface ProductTemplateDownload {
  id: string;
  label: string;
  labels?: Partial<Record<'nl' | 'en' | 'de' | 'fr', string>>;
  url: string;
  fileName: string;
}

export function normalizeTemplateDownloads(input: unknown): ProductTemplateDownload[] {
  if (!Array.isArray(input) || input.length < 1 || input.length > 32) throw new Error('Invalid template collection');
  const ids = new Set<string>();
  return input.map(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) throw new Error('Invalid template');
    const { id, label, url, fileName, labels } = item;
    if (typeof id !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(id) || ids.has(id)) throw new Error('Invalid template ID');
    ids.add(id);
    if (typeof label !== 'string' || !label.trim() || label.length > 240) throw new Error('Invalid template label');
    if (typeof fileName !== 'string' || !/^[^\\/\x00-\x1f"<>:|?*]{1,180}\.pdf$/i.test(fileName)) throw new Error('Invalid template filename');
    if (typeof url !== 'string' || url.length > 2048 || /[\\\x00-\x20]/.test(url)) throw new Error('Invalid template URL');
    if (url.startsWith('/')) {
      if (!url.startsWith('/templates/products/') || /[%?#]/.test(url) || url.split('/').some(part => part === '..' || part === '.') || !/\.pdf$/i.test(url)) throw new Error('Invalid local template URL');
    } else {
      const parsed = new URL(url);
      const trusted = parsed.hostname.endsWith('.public.blob.vercel-storage.com')
        || (parsed.hostname === 's3-eu-west-1.amazonaws.com' && parsed.pathname.startsWith('/pvc-product-production/'));
      if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port || parsed.hash || !trusted || !/\.pdf$/i.test(parsed.pathname)) throw new Error('Untrusted template URL');
    }
    const translated: ProductTemplateDownload['labels'] = {};
    if (labels !== undefined) {
      if (!labels || typeof labels !== 'object' || Array.isArray(labels)) throw new Error('Invalid template labels');
      for (const [language, text] of Object.entries(labels)) {
        if (!['nl', 'en', 'de', 'fr'].includes(language) || typeof text !== 'string' || !text.trim() || text.length > 240) throw new Error('Invalid template labels');
        translated[language as keyof typeof translated] = text.trim();
      }
    }
    return { id, label: label.trim(), url, fileName, ...(labels !== undefined ? { labels: translated } : {}) };
  });
}

export function templateVariantRequest(productSlug: string, variants: readonly Pick<ProductTemplateDownload, 'id'>[] | undefined, selected: string): string | null {
  const base = `/api/template/${encodeURIComponent(productSlug)}`;
  if (variants === undefined) return base;
  return variants.some(item => item.id === selected) ? `${base}?variant=${encodeURIComponent(selected)}` : null;
}

/**
 * Korte zin boven de compacte downloadknop bij de configurator en de
 * uploadstap. Belooft geen bestandsformaat: de server levert een PDF of ZIP.
 */
export function templateDownloadPrompt(locale: string | null | undefined): string {
  if (locale === 'en') return 'Prefer to start from a template? Download the artwork template with the correct dimensions and file specifications.';
  if (locale === 'de') return 'Lieber mit einer Vorlage arbeiten? Laden Sie die Druckvorlage mit den richtigen Maßen und Datenanforderungen herunter.';
  if (locale === 'fr') return 'Vous préférez partir d’un gabarit ? Téléchargez le gabarit avec les bonnes dimensions et les spécifications de fichier.';
  return 'Liever vanuit een sjabloon werken? Download het werkbestand met de juiste maten en aanleverspecificaties.';
}
